import { useEffect, useMemo } from 'react';
import { useUnit } from 'effector-react';
import { folder, useControls } from 'leva';
import type { Schema } from 'leva/dist/declarations/src/types/public.js';

import { $sceneBindings } from '../../state/appState';
import type { NavPoiTunerApi } from '../../scene/createScene';

/**
 * DEV-only Leva panel for live-tuning nav-POI 3D positions on splat scenes
 * with `navigationPois` (currently only `overall`). Reads/writes positions
 * via `SceneBindings.navPoiTuner`. Includes a checkbox for the red debug
 * dot (rendered at each beacon's 3D-anchor projection WITHOUT hover lift —
 * see `PoiMarkerManager.setDebugDotVisible`).
 *
 * Mount alongside `RestrictedCameraTuner` under `?dev`. Renders `null` if
 * the current scene has no nav-POIs (the `useControls` schema is empty in
 * that case — no panel section appears).
 *
 * Tuner values are session-local: position mutations are applied in place
 * to `Sight.position` / `marker.position`, but page reload reverts to the
 * `overall.ts` config. Final tuned values must be copy-pasted back into
 * `navigationPois[*].position`.
 */
export const NavPoiTuner = () => {
  const bindings = useUnit($sceneBindings);
  const tuner: NavPoiTunerApi | null = bindings?.navPoiTuner ?? null;

  // Snapshot initial positions once per tuner instance. Sliders are built
  // off these as default values; subsequent updates flow through onChange
  // → tuner.setNavPoiPosition. If the user also calls `__tuneNavPoi` from
  // the console, the slider's displayed value won't catch up — acceptable
  // for a dev tool.
  const initialPositions = useMemo(() => {
    if (!tuner) return null;
    const map: Record<string, { x: number; y: number; z: number }> = {};
    for (const id of tuner.navPoiSceneIds) {
      const pos = tuner.getNavPoiPosition(id);
      if (pos) map[id] = pos;
    }
    return map;
  }, [tuner]);

  useControls(
    'Nav POIs',
    () => {
      if (!tuner || !initialPositions) return {} as Schema;
      const schema: Schema = {
        'Show debug dot': {
          value: false,
          onChange: (v: boolean) => tuner.setDebugDotVisible(v),
        },
      };
      for (const sceneId of tuner.navPoiSceneIds) {
        const init = initialPositions[sceneId];
        if (!init) continue;
        // Folder name = scene id. Inner keys are prefixed to avoid Leva's
        // flat-key collisions when multiple folders share the same axis.
        // `label` shows the friendly axis letter in the UI.
        schema[sceneId] = folder({
          [`${sceneId}_x`]: {
            label: 'x',
            value: init.x,
            min: -5,
            max: 5,
            step: 0.01,
            onChange: (v: number) => tuner.setNavPoiPosition(sceneId, { x: v }),
          },
          [`${sceneId}_y`]: {
            label: 'y',
            value: init.y,
            min: -0.5,
            max: 1.5,
            step: 0.01,
            onChange: (v: number) => tuner.setNavPoiPosition(sceneId, { y: v }),
          },
          [`${sceneId}_z`]: {
            label: 'z',
            value: init.z,
            min: -5,
            max: 5,
            step: 0.01,
            onChange: (v: number) => tuner.setNavPoiPosition(sceneId, { z: v }),
          },
        });
      }
      return schema;
    },
    [tuner, initialPositions],
  );

  // Hide the debug dot when the tuner unmounts (e.g., scene change).
  useEffect(() => {
    if (!tuner) return;
    return () => {
      tuner.setDebugDotVisible(false);
    };
  }, [tuner]);

  return null;
};

NavPoiTuner.displayName = 'NavPoiTuner';
